import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { blogData } from "../Blog/BlogData"; 
import {
  Calendar,
  User,
  Facebook,
  Twitter,
  Linkedin,
  ArrowLeft,
  Scale,
  MessageSquare,
  Bookmark,
  Share2
} from "lucide-react";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";
import { Helmet } from "react-helmet";

// Updated Palette: Obsidian & Brushed Gold
const COLORS = {
  bgPrimary: "#0a0a0a",   // Dark Obsidian
  bgSecondary: "#141414", // Subtle Gray-Black
  gold: "#c5a059",        // Brushed Gold
  textLight: "#ffffff",
  textMuted: "#9a8a70",   // Golden-Brown Muted
};

const BlogDetails = () => {
  const { blogId } = useParams();
  const blog = blogData.find((b) => b.id === blogId);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [blogId]);

  if (!blog) {
    return (
      <div className="py-40 text-center font-serif min-h-screen flex flex-col justify-center items-center" style={{ backgroundColor: COLORS.bgPrimary, color: COLORS.textLight }}>
        <h2 className="text-4xl font-light mb-6 italic">Document Not Found</h2>
        <Link to="/blogs" style={{ color: COLORS.gold }} className="tracking-[0.3em] uppercase text-xs font-bold border-b border-[#c5a059] pb-1">
          Return to Archives
        </Link>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{blog.title} | Adv Akash Giri</title>
        <meta name="description" content={blog.excerpt} />
        <meta name="author" content="Adv Akash Giri" />
        <meta property="og:title" content={blog.title} />
        <meta property="og:image" content={blog.image} />
      </Helmet>
      
      <Header />

      <div className="pt-44 pb-32 min-h-screen" style={{ backgroundColor: COLORS.bgPrimary }}>
        <div className="max-w-7xl mx-auto px-6">
          
          {/* Back Navigation */}
          <Link
            to="/blogs"
            className="group inline-flex items-center gap-3 mb-16 font-sans uppercase text-[10px] font-bold tracking-[4px] transition-all hover:opacity-70"
            style={{ color: COLORS.gold }}
          >
            <ArrowLeft size={14} className="group-hover:-translate-x-2 transition-transform" />
            Legal Perspectives
          </Link>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">
            {/* LEFT COLUMN: Main Article */}
            <div className="lg:col-span-8">
              <header className="mb-16">
                <div className="flex items-center gap-4 mb-8">
                  <span 
                    className="px-5 py-2 text-[9px] font-bold uppercase tracking-[4px]"
                    style={{ backgroundColor: COLORS.gold, color: "black" }}
                  >
                    {blog.category}
                  </span>
                  <span className="h-[1px] w-12 bg-white/10"></span>
                  <span className="text-[10px] uppercase tracking-[3px] text-gray-500 italic">Case Analysis</span>
                </div>

                <h1 className="text-white text-5xl md:text-7xl font-medium leading-[1.1] mb-12 tracking-tight">
                  {blog.title}
                </h1>

                <div className="flex flex-wrap items-center gap-10 border-y border-white/5 py-10">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full border border-[#c5a059]/30 flex items-center justify-center">
                        <User size={16} style={{ color: COLORS.gold }} />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-gray-500">Author</p>
                        <p className="text-white text-sm font-medium tracking-wide">Adv Akash Giri</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full border border-white/10 flex items-center justify-center">
                        <Calendar size={16} className="text-gray-400" />
                    </div>
                    <div>
                        <p className="text-[10px] uppercase tracking-widest text-gray-500">Published</p>
                        <p className="text-white text-sm font-medium">{blog.date}</p>
                    </div>
                  </div>
                </div>
              </header>

              {/* Featured Image - Portrait focus */}
              <div className="relative mb-20 group">
                <div className="absolute inset-0 border border-[#c5a059] translate-x-3 translate-y-3 -z-10 transition-transform group-hover:translate-x-5 group-hover:translate-y-5 duration-500 opacity-20"></div>
                <div className="overflow-hidden bg-[#141414]">
                    <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full h-[650px] object-cover opacity-80 group-hover:opacity-100 transition-all duration-1000 group-hover:scale-105"
                    />
                </div>
                {/* Decorative Icon */}
                <div className="absolute -bottom-12 -left-12 opacity-[0.03] pointer-events-none">
                  <Scale size={280} color={COLORS.gold} />
                </div>
              </div>

              {/* Article Content */}
              <div className="article-body-wrapper">
                <div
                  className="prose prose-invert prose-gold max-w-none"
                  dangerouslySetInnerHTML={{ __html: blog.content }}
                />
              </div>

              {/* Signature & Share */}
              <div className="mt-28 pt-12 border-t border-white/5 flex flex-col md:flex-row items-center justify-between gap-10">
                <div className="text-center md:text-left">
                  <p className="text-[#c5a059] font-serif italic text-2xl mb-2">Adv. Akash Giri</p>
                  <p className="text-[10px] text-gray-500 uppercase tracking-[4px] font-sans font-bold">Supreme Court of India • Legal Strategist</p>
                </div>
                <div className="flex items-center gap-6">
                  <span className="text-[10px] uppercase tracking-widest text-gray-600 mr-2 flex items-center gap-2">
                    <Share2 size={12} /> Share Insights:
                  </span>
                  {[Facebook, Twitter, Linkedin].map((Icon, idx) => (
                      <Icon key={idx} size={18} className="text-gray-500 hover:text-[#c5a059] transition-all cursor-pointer hover:-translate-y-1" />
                  ))}
                </div>
              </div>
            </div>

            {/* RIGHT COLUMN: Professional Sidebar */}
            <div className="lg:col-span-4">
              <div className="sticky top-32 space-y-12">
                
                {/* ADV Akash Giri Card */}
                <div className="p-12 bg-[#111] border border-white/5 relative overflow-hidden group shadow-2xl">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-[#c5a059]/5 rounded-full -mr-12 -mt-12 transition-transform duration-700 group-hover:scale-150"></div>
                  
                  <h3 className="text-xl font-bold mb-8 flex items-center gap-3 text-white">
                    <span className="w-8 h-[1px] bg-[#c5a059]"></span>
                    Legal Advisory
                  </h3>
                  
                  <p className="text-sm text-[#9a8a70] font-sans mb-12 leading-relaxed italic font-light">
                    "Deep legal understanding combined with strategic thinking ensures that justice is not just a concept, but a delivered reality."
                  </p>

                  <Link to="/contact-us" className="block">
                    <button 
                        className="w-full bg-[#c5a059] text-black font-bold py-5 uppercase text-[10px] tracking-[4px] transition-all duration-300 hover:bg-white"
                    >
                      Request Consultation
                    </button>
                  </Link>

                  <div className="mt-10 space-y-4">
                    <div className="flex items-center gap-4 text-[10px] uppercase tracking-[2px] text-gray-500 border-t border-white/5 pt-8">
                       <MessageSquare size={14} className="text-[#c5a059]" /> Private Consultations Only
                    </div>
                  </div>
                </div>

                {/* Categories / Areas of Practice */}
                <div className="p-10 border border-white/5 bg-[#0f0f0f]">
                  <h4 className="font-bold text-white mb-8 text-[10px] uppercase tracking-[3px] flex items-center justify-between">
                    Practice Areas <Bookmark size={14} className="text-[#c5a059]" />
                  </h4>
                  <div className="grid grid-cols-1 gap-3">
                    {["Constitutional Law", "Corporate Litigation", "Matrimonial Matters", "Criminal Defense"].map((tag) => (
                      <div
                        key={tag}
                        className="text-[11px] py-4 border-b border-white/5 text-gray-400 hover:text-[#c5a059] cursor-pointer transition-all flex justify-between group"
                      >
                        {tag}
                        <ArrowLeft size={12} className="rotate-180 opacity-0 group-hover:opacity-100 transition-all -translate-x-4 group-hover:translate-x-0" />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* --- DYNAMIC JOURNAL CSS --- */}
        <style dangerouslySetInnerHTML={{ __html: `
            .prose-gold h2 {
              font-family: 'serif';
              color: white;
              font-size: 2.25rem;
              margin-top: 3.5rem;
              margin-bottom: 1.5rem;
              font-weight: 400;
              letter-spacing: -0.02em;
            }
            .prose-gold p {
              font-family: 'Inter', sans-serif;
              color: ${COLORS.textMuted};
              line-height: 1.9;
              font-size: 1.15rem;
              margin-bottom: 2rem;
              font-weight: 300;
            }
            .prose-gold strong {
              color: ${COLORS.gold};
              font-weight: 600;
            }
            .prose-gold blockquote {
              border-left: 2px solid ${COLORS.gold};
              background: #111;
              padding: 3rem;
              margin: 4rem 0;
              font-style: italic;
              color: white;
              font-size: 1.5rem;
              line-height: 1.6;
              border-radius: 4px;
            }
            .prose-gold ul {
              margin: 2rem 0;
              list-style-type: none;
            }
            .prose-gold li {
              color: ${COLORS.textMuted};
              margin-bottom: 1.25rem;
              padding-left: 1.5rem;
              position: relative;
            }
            .prose-gold li::before {
              content: "•";
              color: ${COLORS.gold};
              position: absolute;
              left: 0;
              font-weight: bold;
            }
        `}} />
      </div>

      <Footer />
    </>
  );
};

export default BlogDetails;